import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Button,
  Modal,
  Form,
  Accordion,
  Alert,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaCarSide,
  FaStar,
  FaArrowRight,
  FaMoneyBillWave,
  FaFileInvoice,
  FaTruckLoading,
  FaTruckMoving,
  FaFilePdf,
  FaArrowLeft,
  FaTruck,
  FaMapMarkerAlt,
  FaPlus,
  FaMinus,
  FaStickyNote,
  FaLocationArrow,
  FaTrash,
} from "react-icons/fa";
import person from "../../assets/images/person.png";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI } from "../../api/apiSystem";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import ClientChatBox from "../chat/ClientChatBox";
import { formatNumber } from "../../utils/helpers";
import "./DarkClientStyles.css";
import XRechnungViewer from "./XRechnungViewer";


export default function ClientDetails() {
  const { t } = useTranslation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.fromPage || 1;

  // 🔸 Rating Modal states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [showXRechnung, setShowXRechnung] = useState(false);

  // 🔸 Note Modal states
  const [note, setNote] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [noteAlert, setNoteAlert] = useState({ show: false, message: "", variant: "" });

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null
  );

  const getExtraServices = () => {
    const services = booking?.extra_service;
    if (!services) return [];

    return Array.isArray(services) ? services : [services];
  };

  const getExtraServiceLabel = (service) => {
    const labels = {
      loading: t("loading_service"),
      unloading: t("unloading_service"),
    };

    return labels[service] || t(service);
  };

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const res = await userAPI.bookings.show(id);
        if (res.success) {
          setBooking(res.data.data || res.data);
          setNote(res.data.data?.user_note || '');
        } else {
          console.error("Failed to fetch booking:", res);
        }
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const handleSubmitRating = async () => {
    try {
      setSubmitting(true);
      const res = await userAPI.bookingOrders.rating(id, {
        rating: selectedRating,
        comment: comment,
      });
      if (res.success) {
        alert(t('clientDetails.rating_modal.success'));
        setShowRatingModal(false);
        const updated = await userAPI.bookings.show(id);
        setBooking(updated.data.data || updated.data);
      } else {
        alert(t('clientDetails.rating_modal.error'));
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendNote = async () => {
    try {
      setSendingNote(true);
      const response = await userAPI.bookings.sendNote(id, { user_note: note });

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t('success'),
          text: t('clientDetails.note.success'),
          timer: 3000,
          showConfirmButton: false,
        });
        const updated = await userAPI.bookings.show(id);
        setBooking(updated.data.data || updated.data);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t('error'),
        text: err.message || t('clientDetails.note.error'),
        timer: 3000,
      });
    } finally {
      setSendingNote(false);
    }
  };

  // back button to bookings table page
  // const handleBack = () => {
  //   navigate('/user/booking', { 
  //     state: { fromPage: fromPage } 
  //   });
  // };

  const handleBack = () => {
    const state = {
      fromPage: location.state?.fromPage || 1,
      currentPage: location.state?.currentPage || 1,
      highlightId: location.state?.highlightId || parseInt(id),
      scrollToHighlight: true,
      activeTab: location.state?.activeTab || 'active'
    };

    navigate('/user/booking', {
      state: state
    });
  };

  // navigate to track page
  const handleViewTrack = (booking_id) => {
    navigate(`/user/booking/booking_details/track/${booking_id}`);
  };

  // navigate to live track page
  const handleViewLiveTrack = (id) => {
    navigate(`/user/booking/booking_details/live-track/${id}`);
  };

  const canDeleteBooking = () => {
    const deletableStatuses = ["pending", "approved"];
    const acceptedOrderStatuses = ["assigned", "received", "completed"];

    return (
      deletableStatuses.includes(booking?.status) &&
      Number(booking?.order_status) !== 1 &&
      !acceptedOrderStatuses.includes(booking?.order?.status)
    );
  };

  const handleDeleteBooking = async () => {
    const reasonResult = await Swal.fire({
      icon: "warning",
      title: t("clientTable.deleteModal.deleteBooking"),
      text: t("clientTable.deleteModal.confirmDelete"),
      input: "textarea",
      inputPlaceholder: t("clientTable.deleteModal.reasonPlaceholder"),
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("clientTable.buttons.confirmDelete"),
      cancelButtonText: t("clientTable.buttons.cancel"),
      inputValidator: (value) => {
        if (!value?.trim()) {
          return t("clientTable.deleteModal.reasonRequired");
        }
        return undefined;
      },
    });

    if (!reasonResult.isConfirmed) {
      return;
    }

    try {
      Swal.fire({
        title: t("clientTable.deleteModal.deleting"),
        text: t("clientTable.deleteModal.pleaseWait"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await userAPI.bookings.delete(id, {
        deleted_reason: reasonResult.value.trim(),
      });

      if (!response.success) {
        throw new Error(response.message || t("clientTable.deleteModal.failedToDelete"));
      }

      await Swal.fire({
        icon: "success",
        title: t("clientTable.deleteModal.success"),
        text: t("clientTable.deleteModal.deletedSuccessfully"),
        confirmButtonColor: "#ff7b00",
      });

      navigate("/user/booking", {
        state: {
          fromPage,
          currentPage: fromPage,
          activeTab: "booking",
        },
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("clientTable.deleteModal.deleteFailed"),
        text: err.message || t("clientTable.deleteModal.deletionError"),
        confirmButtonColor: "#ff7b00",
      });
    }
  };

  // دالة لمعالجة البيانات المصفوفة
  const getArrayData = (data, index) => {
    if (Array.isArray(data)) {
      return data[index] || '-';
    }
    return data || '-';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (!booking) {
    return (
      <Container className="py-5 text-center">
        <Card className="shadow-sm p-4">
          <h5 className="text-danger">🚫 {t('clientDetails.alerts.not_found.title')}</h5>
          <p className="text-muted">
            {t('clientDetails.alerts.not_found.message')}
          </p>
        </Card>
      </Container>
    );
  }

  const driver = booking.order || {};
  const rating = booking.rating || {};
  const orderStatus = booking?.order?.status?.toLowerCase();
  const showLiveTracking = Boolean(booking?.order) && !["pending", "completed"].includes(orderStatus);

  // حساب عدد أماكن التحميل والتنزيل
  const loadingCount = Array.isArray(booking.booking_from) ? booking.booking_from.length : 0;
  const unloadingCount = Array.isArray(booking.booking_to) ? booking.booking_to.length : 0;

  const newLocal = "#ff7b00";
  return (
    <>
      <NavbarMenu />
      <div className="transport-details-page">
        <Container className="py-5">

          {/* <Card className="border-0 rounded-3 p-4">
            <Card.Body> */}
              <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
                <Button variant="secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t('clientDetails.back')}
                </Button>

                <Button
                  variant="outline"
                  style={{ color: newLocal, borderColor: newLocal }}
                  size="sm"
                  onClick={() => handleViewTrack(booking.booking_id)}
                >
                  <FaTruck className="me-1" /> {t('clientDetails.track')}
                </Button>

                {showLiveTracking && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleViewLiveTrack(booking.order.id)}
                  >
                    <FaLocationArrow className="me-1" /> {t('clientDetails.live_tracking')}
                  </Button>
                )}

                {canDeleteBooking() && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDeleteBooking}
                  >
                    <FaTrash className="me-1" />
                    {t("clientTable.buttons.deleteOrder")}
                  </Button>
                )}
              </div>
              {/* 🔹 رقم الطلب وحالة */}
              <Row className="align-items-center mb-3">
                <Col>
                  <h5 className="fw-bold text-orange">
                    {t('clientDetails.order')} : {booking.booking_id}
                  </h5>
                </Col>
                <Col className="text-end">
                  <Badge
                    bg={
                      booking.status === "completed"
                        ? "success"
                        : booking.status === "pending"
                          ? "warning"
                          : "secondary"
                    }
                    className="px-3 py-2"
                  >
                    {t(`clientDetails.status.${booking.status}`) || t('clientDetails.status.unknown')}
                  </Badge>
                </Col>
              </Row>

              {/* 🔹 Progress Status */}
              <div className="progress-container">
                <div className="d-flex justify-content-between align-items-start">
                  {/* Created */}
                  <div className="text-center">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto ${booking.status === "assigned" ||
                        booking.status === "received" ||
                        booking.status === "completed"
                        ? "bg-success-subtle"
                        : ""
                        }`}
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "rgba(239, 237, 237, 1)",
                      }}
                    >
                      <FaCheckCircle
                        size={24}
                        className={
                          booking.status === "approved" ||
                            booking.status === "assigned" ||
                            booking.status === "received" ||
                            booking.status === "completed"
                            ? "text-success"
                            : "text-secondary"
                        }
                      />
                    </div>
                    <p className="mb-0 small">{t('clientDetails.progress.created')}</p>
                  </div>

                  <div
                    className={`flex-grow-1 border-top mx-2 ${booking.status === "received" || booking.status === "completed"
                      ? "border-success"
                      : ""
                      }`}
                    style={{ marginTop: "24px" }}
                  ></div>

                  {/* Unterwegs */}
                  <div className="text-center">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto ${booking.status === "received" || booking.status === "completed"
                        ? "bg-success-subtle"
                        : ""
                        }`}
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "rgba(217, 217, 217, 0.51)",
                      }}
                    >
                      <FaCarSide
                        size={24}
                        className={
                          booking.status === "received" || booking.status === "completed"
                            ? "text-success"
                            : "text-secondary"
                        }
                      />
                    </div>
                    <p className="mb-0 small">{t('clientDetails.progress.on_the_way')}</p>
                  </div>

                  <div
                    className={`flex-grow-1 border-top mx-2 ${booking.status === "completed"
                      ? "border-success"
                      : ""
                      }`}
                    style={{ marginTop: "24px" }}
                  ></div>

                  {/* Completed */}
                  <div className="text-center">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto ${booking.status === "completed"
                        ? "bg-success-subtle"
                        : ""
                        }`}
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "rgba(239, 237, 237, 1)",
                      }}
                    >
                      <FaCheckCircle
                        size={24}
                        className={
                          booking.status === "completed"
                            ? "text-success"
                            : "text-secondary"
                        }
                      />
                    </div>
                    <p className="mb-0 small">{t('clientDetails.progress.completed')}</p>
                  </div>
                </div>
              </div>

              {/* 🔹 Locations */}
              <Row className="mb-4">
                <Col md={6}>
                  <h6 className="fw-bold text-orange mb-3">
                    {t('clientDetails.pickup.title')} ({loadingCount} {t('clientDetails.pickup.locations')})
                  </h6>
                  <Accordion defaultActiveKey={Array.from({ length: loadingCount }, (_, i) => i.toString())}>
                    {Array.from({ length: loadingCount }).map((_, index) => (
                      <Accordion.Item key={index} eventKey={index.toString()}>
                        <Accordion.Header>
                          <FaMapMarkerAlt className="me-2 text-primary" />
                          {t('clientDetails.pickup.location')} {index + 1}: {getArrayData(booking.booking_from, index)}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Card className="border-0 p-3">
                            <p><strong>{t('clientDetails.pickup.company')}:</strong> {getArrayData(booking.loading_company, index)}</p>
                            <p><strong>{t('clientDetails.pickup.name')}:</strong> {getArrayData(booking.loading_first_name, index)} {getArrayData(booking.loading_last_name, index)}</p>
                            <p><strong>{t('clientDetails.pickup.address')}:</strong> {getArrayData(booking.loading_streat_no, index)}</p>
                            <p><strong>{t('clientDetails.pickup.additional_address')}:</strong> {getArrayData(booking.loading_address_additional, index)}</p>
                            <p><strong>{t('clientDetails.pickup.postal_code')}:</strong> {getArrayData(booking.loading_postal, index)}</p>
                            <p><strong>{t('clientDetails.pickup.phone')}:</strong> {getArrayData(booking.loading_phone, index)}</p>
                            <p><strong>{t('clientDetails.pickup.reference')}:</strong> {getArrayData(booking.loading_reffernce, index)}</p>
                            <p><strong>{t('clientDetails.pickup.date_time')}:</strong> {getArrayData(booking.loading_date_time, index)}</p>
                          </Card>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Col>
                <Col md={6}>
                  <h6 className="fw-bold text-orange mb-3">
                    {t('clientDetails.delivery.title')} ({unloadingCount} {t('clientDetails.delivery.locations')})
                  </h6>
                  <Accordion defaultActiveKey={Array.from({ length: unloadingCount }, (_, i) => i.toString())}>
                    {Array.from({ length: unloadingCount }).map((_, index) => (
                      <Accordion.Item key={index} eventKey={index.toString()}>
                        <Accordion.Header>
                          <FaMapMarkerAlt className="me-2 text-success" />
                          {t('clientDetails.delivery.location')} {index + 1}: {getArrayData(booking.booking_to, index)}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Card className="border-0 p-3">
                            <p><strong>{t('clientDetails.delivery.company')}:</strong> {getArrayData(booking.unloading_company, index)}</p>
                            <p><strong>{t('clientDetails.delivery.name')}:</strong> {getArrayData(booking.unloading_first_name, index)} {getArrayData(booking.unloading_last_name, index)}</p>
                            <p><strong>{t('clientDetails.delivery.address')}:</strong> {getArrayData(booking.unloading_streat_no, index)}</p>
                            <p><strong>{t('clientDetails.delivery.additional_address')}:</strong> {getArrayData(booking.unloading_address_additional, index)}</p>
                            <p><strong>{t('clientDetails.delivery.postal_code')}:</strong> {getArrayData(booking.unloading_postal, index)}</p>
                            <p><strong>{t('clientDetails.delivery.phone')}:</strong> {getArrayData(booking.unloading_phone, index)}</p>
                            <p><strong>{t('clientDetails.delivery.reference')}:</strong> {getArrayData(booking.unloading_reffernce, index)}</p>
                            <p><strong>{t('clientDetails.delivery.date_time')}:</strong> {getArrayData(booking.unloading_date_time, index)}</p>
                          </Card>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Col>
              </Row>

              {/* 🔹 Sendung */}
              <h6 className="fw-bold text-orange mb-2">{t('clientDetails.shipment.title')}</h6>
              <Card
                className="border-0 mb-4"
                style={{ background: "rgba(239, 237, 237, 1)" }}
              >
                <Table bordered responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('clientDetails.shipment.article')}</th>
                      <th>{t('clientDetails.shipment.weight')}</th>
                      <th>{t('clientDetails.shipment.dimensions')}</th>
                      <th>{t('clientDetails.shipment.quantity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {booking.shipment_data?.map((item, i) => (
                      <tr key={i}>
                        <td>{t(item.shipment_data)}</td>
                        {item.shipment_data != "dokument" ? (
                          <>
                            <td>{formatNumber(item.weight)} kg</td>
                            <td>
                              {item.length}x{item.width}x{item.height} cm
                            </td>
                          </>
                        ) : (
                          <>
                            <td>-</td>
                            <td>-</td>
                          </>
                        )}
                        <td>{item.quantity || 1}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3} className="fw-bold">
                        {t('clientDetails.shipment.total_weight')}:
                      </td>
                      <td className="fw-bold text-orange">
                        {formatNumber(booking.total_weight) || "-"} Kg
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="fw-bold">
                        {t('clientDetails.shipment.distance')}:
                      </td>
                      <td className="fw-bold">
                        {booking.distance || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="fw-bold">
                        {t('clientDetails.shipment.duration')}:
                      </td>
                      <td className="fw-bold">
                        {booking.total_duration || "-"}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card>

              {/* 🔹 Fahrzeugtyp */}
              <h6 className="fw-bold text-orange mb-2">{t('clientDetails.vehicle.title')}</h6>
              <Card
                className="border-0 p-3 mb-4"
              >
                <p className="fw-semibold mb-1">{booking.vehicle_name} 
                  {Number(booking.vehicle_id) === 4 &&
                    ` (${booking.crane === "yes"
                    ? t("driverCard.general.withCrane", "Mit Hebebühne")
                    : t("driverCard.general.withoutCrane", "Ohne Hebebühne")})`}
                </p>

                <p className="text-muted small mb-1">{booking.vehicle_size}</p>
              </Card>

              {/* 🔹 Zusatzinfo & Fahrer */}
              <Row>
                <Col md={8}>
                  <Card
                    className="border-0 p-3 mb-4"
                  >
                    <h6 className="fw-bold text-orange mb-2">
                      ℹ️ {t('clientDetails.additional_info.title')}
                    </h6>
                    <p className="text-muted">{booking.user_note || "-"}</p>
                    <div className="mt-3">
                      <strong>{t("extra_services")}:</strong>
                      {getExtraServices().length > 0 ? (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {getExtraServices().map((service) => (
                            <Badge key={service} bg="warning" text="dark">
                              {getExtraServiceLabel(service)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted ms-2">{t("no_extra_services")}</span>
                      )}
                    </div>
                    <hr />
                    <Row className="mt-3 align-items-start text-center">
                      <Col>
                        <p className="mb-0">
                          {t('clientDetails.additional_info.pickup_time')}:{" "}
                          <strong>{getArrayData(booking.loading_date_time, 0) || "-"}</strong>
                        </p>
                      </Col>
                      <Col xs="auto">
                        <FaArrowRight className="text-orange" size={20} />
                      </Col>
                      <Col>
                        <p className="mb-0">
                          {t('clientDetails.additional_info.delivery_time')}:{" "}
                          <strong>
                            {getArrayData(booking.unloading_date_time, 0) || "-"}
                          </strong>
                        </p>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card
                    className="border-0 h-100 p-3"
                  >
                    <h6 className="fw-bold text-center text-warning mb-3">
                      👨‍✈️ {t('clientDetails.driver.title')}
                    </h6>

                    <div className="text-center mb-3">
                      {booking.status === "completed" && (
                        <div className="mb-2">
                          {!rating.value || rating.value === 0 ? (
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => setShowRatingModal(true)}
                            >
                              {t('clientDetails.driver.give_rating')}
                            </Button>
                          ) : (
                            [...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                size={18}
                                className={`me-1 ${i < rating.value ? "text-warning" : "text-muted"
                                  }`}
                              />
                            ))
                          )}
                        </div>
                      )}
                      {booking.status === "completed" && rating.comment && (
                        <p className="small text-muted fst-italic mb-0">
                          &quot;{rating.comment}&quot;
                        </p>
                      )}
                    </div>

                    {booking.order ? (
                      <>
                        <div className="text-center">
                          <h6 className="mb-0">
                            {driver.driver_name || t('clientDetails.driver.unknown')}
                          </h6>
                          <p className="text-muted small mb-2">
                            {driver.phone_number || "-"}
                          </p>
                        </div>

                        <ul className="list-unstyled small mb-3 text-center mt-2">
                          <li>{t('clientDetails.driver.vehicle')}: {driver.vehicle_type || "-"}</li>
                          <li>{t('clientDetails.driver.number')}: {driver.vehicle_number || "-"}</li>
                          <li>{t('clientDetails.driver.location')}: {driver.current_location || "-"}</li>
                          {/* <li>{t('clientDetails.driver.price')}: {driver.price || "-"} €</li> */}
                          <li>{t('clientDetails.driver.status')}: {driver.status || "N/A"}</li>
                        </ul>
                      </>
                    ) : (
                      <p className="text-muted text-center">{t('clientDetails.driver.no_driver_assigned')}</p>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* 🔹 Rechnungsdetails */}
              <h6 className="fw-bold text-orange">
                <FaFileInvoice className="me-2" /> {t('clientDetails.invoice.title')}
              </h6>
              <Card
                className="border-0 mb-4 p-3"
              >
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>{t('clientDetails.invoice.title_field')}:</strong> {booking.invoice_title}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.first_name')}:</strong> {booking.invoice_first_name}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.last_name')}:</strong> {booking.invoice_last_name}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.company')}:</strong> {booking.invoice_company}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.email')}:</strong> {booking.invoice_email}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.email_2')}:</strong> {booking.invoice_email_2}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>{t('clientDetails.invoice.street')}:</strong> {booking.invoice_street_no}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.additional_address')}:</strong>{" "}
                      {booking.invoice_addition_address}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.postal_city')}:</strong> {booking.invoice_postal_code}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.phone')}:</strong> {booking.invoice_phone_number}
                    </p>
                    <p>
                      <strong>{t('clientDetails.invoice.reference')}:</strong> {booking.invoice_reffernce}
                    </p>
                  </Col>
                </Row>
              </Card>

              {/* 🔹 Dokumente */}
              <h6 className="fw-bold text-orange">
                <FaFilePdf className="me-2" /> {t('clientDetails.documents.title')}
              </h6>
              <Card
                className="border-0 mb-5 p-3 text-center"
              >
                {booking.invoice_path ? (
                  <div className="d-flex gap-2">

                    {booking.show_invoice && (
                      <Button
                        className="btn-light"
                        size="sm"
                        onClick={() => window.open(booking.invoice_path, '_blank')}
                      >
                        <FaFileInvoice className="me-2" />
                        {t('clientDetails.documents.view_invoice')}
                      </Button>
                    )}

                    {/* {booking.xrechnung_path && (
                      <Button
                        className="btn-light"
                        size="sm"
                        onClick={() => setShowXRechnung(true)}
                      >
                        <FaFileInvoice className="me-2" style={{ color: '#ff7b00' }} />
                        {t('clientDetails.documents.view_xrechnung', 'XRechnung')}
                      </Button>
                    )} */}

                    {booking.bill_path && booking.bill_path.map((billUrl, index) => (
                      <Button
                        key={index}
                        className="btn-light me-2"
                        size="sm"
                        onClick={() => window.open(billUrl, '_blank')}
                      >
                        <FaFileInvoice className="me-2" />
                        CMR
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">{t('clientDetails.documents.no_documents')}</p>
                )}
              </Card>

              {/* 🔹 Zahlungsdetails */}
              <h6 className="fw-bold text-orange mt-4">
                <FaMoneyBillWave className="me-2" /> {t('clientDetails.payment.title')}
              </h6>
              <Card
                className="border-0 mb-4 p-3"
              >
                <Row>
                  <p className="text-center">
                    <strong> {booking.sub_total} € {t('clientDetails.payment.netto')}</strong> <br />
                    {t('clientDetails.payment.plus_vat')} <strong> {booking.total} € {t('clientDetails.payment.brutto')}</strong>
                  </p>
                </Row>
                <Row>
                  <Col md={12}>
                    <p>
                      <strong>{t('clientDetails.payment.status')}:</strong>{" "}
                      <Badge
                        bg={
                          booking.payment_status === 1 ? "success" : "secondary"
                        }
                      >
                        {booking.payment || t('clientDetails.payment.unpaid')}
                      </Badge>
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>{t('clientDetails.payment.method')}:</strong> {booking.payment_method || "-"}
                    </p>
                  </Col>
                </Row>
              </Card>

              <Card className="mb-4">
                <Card.Header className="fw-bold card-title">{t('clientDetails.note.title')}</Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group controlId="additionalDetails">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder={t('clientDetails.note.placeholder')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={sendingNote}
                      />
                    </Form.Group>
                    <div className="mt-3 d-flex justify-content-end">
                      <Button variant="orange" className="update-btn" onClick={handleSendNote} disabled={sendingNote}>
                        {sendingNote ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('clientDetails.note.sending')}
                          </>
                        ) : (
                          t('clientDetails.note.send_button')
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

            {/* </Card.Body>
          </Card> */}
        </Container>
      </div>


      <XRechnungViewer
        show={showXRechnung}
        onHide={() => setShowXRechnung(false)}
        xmlUrl={booking.xrechnung_path}
        title={`XRechnung ${booking.booking_id}`}
      />

      {/* ⭐ Rating Modal */}
      <Modal
        show={showRatingModal}
        onHide={() => setShowRatingModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('clientDetails.rating_modal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            {[...Array(5)].map((_, index) => (
              <FaStar
                key={index}
                size={30}
                className={`me-2 ${index < selectedRating ? "text-warning" : "text-muted"
                  }`}
                onClick={() => setSelectedRating(index + 1)}
                style={{ cursor: "pointer" }}
              />
            ))}
          </div>
          <Form.Group>
            <Form.Label>{t('clientDetails.rating_modal.comment')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('clientDetails.rating_modal.comment_placeholder')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRatingModal(false)}>
            {t('clientDetails.rating_modal.cancel')}
          </Button>
          <Button
            variant="warning"
            onClick={handleSubmitRating}
            disabled={submitting}
          >
            {submitting ? t('clientDetails.rating_modal.submitting') : t('clientDetails.rating_modal.submit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}






